/**
 * `/metal-resources/metal-market-intelligence/podcast/<slug>/`: one Cup O' Joe episode's page
 * (video, platform links, summary, guests, transcript), rendered by
 * `components/Blog/PodcastDetail.tsx`. Ryerson build only. Every episode page is built at build
 * time; there is no runtime fetch.
 *
 * How the pages are built:
 * - `getStaticPaths` takes every `podcast` slug from `src/contentful/shared/index.tsx` →
 *   `getAllContentfulPaths("podcast")`, URI-encoded, with `fallback: false`. That request is
 *   unpaged, so it reads Contentful's first 100 entries.
 * - `getStaticProps` URI-decodes the slug and calls
 *   `src/contentful/podcast/getContentfulPodcastDetails.tsx` →
 *   `getContentFulPodcastEpisodeDetails`. That fetcher reads the whole collection once per Node
 *   process and throws, failing the build, when the slug is missing in any locale.
 * - `getStaticProps` has no brand check of its own. On any other brand `getStaticPaths` returns no
 *   paths, so it never runs.
 *
 * What the page shows per language comes from the fetcher. Its header records that the `fr` and
 * `es` entries are swapped there (French visitors get the es-MX content).
 *
 * Related: `./index.tsx` (the episode list that links here),
 * `src/contentful/podcast/types/index.ts` → `TranslatedPodcastDetails` (the props shape).
 */
import React from "react";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import MetaHead, { MetaContent } from "@components/Shared/model/MetaHead";
import { TranslatedPodcastDetails } from "@contentful/podcast/types";
import { useApplication } from "@ryerson/frontend.application";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import PodcastDetail from "@components/Blog/PodcastDetail";
import { LanguageContent } from "@components/Shared/model/LanguageContent";
import { getContentFulPodcastEpisodeDetails } from "@contentful/podcast/getContentfulPodcastDetails";
import { getAllContentfulPaths } from "@contentful/shared";

/** The page's own props, beside the shared header and footer props. */
type PodcastDetailsProps = {
	/** The episode's details per language, from `getContentFulPodcastEpisodeDetails`. */
	podcastDetailsData: TranslatedPodcastDetails;
};

/**
 * One path per Contentful `podcast` slug on the Ryerson build; no paths, and no Contentful call,
 * on any other brand. Any other slug is a 404 (`fallback: false`).
 *
 * @throws The Contentful or network error from `getAllContentfulPaths`, which fails the build.
 */
export const getStaticPaths = async () => {
	// skip page generation
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		// If company is not ryerson, in local dev (yarn start) next.js will still try to serve the page for any URL but since getStaticProps will not run, the page will break.
		// In production (static export), unknown paths return 404.html — so this is a local dev-only quirk.
		return { paths: [], fallback: false };
	}

	let pathSlugs = await getAllContentfulPaths("podcast");

	let paths = pathSlugs.map((slugString) => ({
		params: {
			slug: encodeURI(slugString),
		},
	}));

	return {
		paths,
		fallback: false,
	};
};

/**
 * Builds one episode page: the shared header and footer props plus `podcastDetailsData`.
 *
 * @throws When the episode is missing in any locale, or on a Contentful or network error. Either
 *   fails the build.
 */
export const getStaticProps = async ({ params }: any) => {
	let { props: headerProps } = await allContentfulHeaderCalls();
	let podcastDetailsData = await getContentFulPodcastEpisodeDetails(decodeURI(params.slug));
	return { props: { ...headerProps, podcastDetailsData } };
};

/**
 * The episode page: SEO head for the visitor's language, then the episode itself.
 */
export default function Page(props: HeaderPageProps & PodcastDetailsProps) {
	const {
		localization: { language },
	} = useApplication();

	let podcastDetailsData = props.podcastDetailsData;
	// The `|| {}` doesn't make missing data safe: `enContent.seoTitle` below throws on it.
	let { en: enContent, fr: frContent, es: esContent } = podcastDetailsData || {};

	// SEO head per language. The helper stringifies `seoTitle` and `seoDescription`
	// (`src/contentful/podcast/helpers/index.tsx` → `processContentFulEpisodesDetailsData`),
	// so one left unset in Contentful arrives as the string "undefined", which is truthy: the
	// `title` and `summary` fallbacks never apply. All three languages use the English slug for
	// the canonical URL and the English thumbnail for the share image.
	let MetaObject: LanguageContent<MetaContent> = {
		en: {
			title: enContent.seoTitle || enContent.title,
			description: enContent.seoDescription || enContent.summary?.summary,
			url: "/metal-resources/metal-market-intelligence/podcast/" + podcastDetailsData.en.slug,
			imageUrl: enContent.videoThumbnail?.file.url,
			company: false,
		},
		fr: {
			title: frContent.seoTitle || frContent?.title,
			description: frContent.seoDescription || frContent.summary?.summary,
			url: "/metal-resources/metal-market-intelligence/podcast/" + podcastDetailsData.en.slug,
			imageUrl: enContent.videoThumbnail?.file.url,
			company: false,
		},
		es: {
			title: esContent.seoTitle || esContent.title,
			description: esContent.seoDescription || esContent.summary?.summary,
			url:
				"/metal-resources/metal-market-intelligence/podcast/" + podcastDetailsData.en?.slug,
			imageUrl: enContent.videoThumbnail?.file.url,
			company: false,
		},
	};

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<PodcastDetail {...podcastDetailsData} />
		</HeaderLayout>
	);
}
